import { useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, CheckCircle, Eye, AlertCircle, Clock, Building2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useNavigate } from "react-router-dom";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { validatePassword } from "@/lib/validations";

interface FleetDemoModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

interface DemoData {
  full_name: string;
  email: string;
  password: string;
}

export function FleetDemoModal({ open, onOpenChange }: FleetDemoModalProps) {
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const { toast } = useToast();
  const navigate = useNavigate();
  

  const [data, setData] = useState<DemoData>({
    full_name: "",
    email: "",
    password: ""
  });

  const handleInputChange = (field: keyof DemoData, value: string) => {
    setData(prev => ({ ...prev, [field]: value }));
  };

  const isFormValid = () => {
    return data.full_name && data.email && validatePassword(data.password).isValid;
  };

  const handleSubmit = async () => {
    if (!isFormValid()) return;

    setLoading(true);
    try {
      const { data: result, error } = await supabase.functions.invoke('create-demo-user', {
        body: {
          email: data.email,
          password: data.password,
          full_name: data.full_name
        }
      });

      if (error) throw error;

      if (result?.success) {
        setSuccess(true);
        toast({
          title: "Acesso demo criado!",
          description: result.message || "Seu acesso foi criado. Faça login para explorar o sistema!",
        });

        // Reset form after 2 seconds and redirect to home
        setTimeout(() => {
          handleClose();
          navigate('/');
        }, 2000);
      } else {
        throw new Error(result?.error || 'Erro ao criar acesso demo');
      }
    } catch (error: any) {
      console.error('Error creating demo access:', error);
      toast({
        title: "Erro ao criar acesso",
        description: error.message || "Ocorreu um erro ao criar seu acesso demo. Tente novamente.",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setData({ full_name: "", email: "", password: "" });
    setSuccess(false);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-2xl">
            <Eye className="w-6 h-6 text-primary" />
            Demonstração FleetONE
          </DialogTitle>
          <DialogDescription className="text-base">
            Explore o sistema com dados reais por 7 dias! Acesso somente leitura à Empresa de Demonstração.
          </DialogDescription>
        </DialogHeader>

        {success ? (
          <div className="flex flex-col items-center justify-center py-8 space-y-4">
            <CheckCircle className="w-16 h-16 text-success" />
            <h3 className="text-xl font-semibold">Acesso demo criado!</h3>
            <p className="text-muted-foreground text-center">
              Redirecionando para o login...
            </p>
          </div>
        ) : (
          <div className="space-y-6 py-4">
            <Alert className="bg-amber-50 border-amber-200 dark:bg-amber-950 dark:border-amber-800">
              <AlertCircle className="h-4 w-4 text-amber-600 dark:text-amber-400" />
              <AlertDescription className="text-amber-800 dark:text-amber-200">
                <strong>Acesso de visualização apenas.</strong> Para testar todas as funcionalidades, inicie um teste grátis de 30 dias.
              </AlertDescription>
            </Alert>

            <div className="space-y-2">
              <Label htmlFor="full_name">Nome Completo *</Label>
              <Input
                id="full_name"
                type="text"
                value={data.full_name}
                onChange={(e) => handleInputChange('full_name', e.target.value)}
                placeholder="Seu nome completo"
                disabled={loading}
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="email">Email *</Label>
              <Input
                id="email"
                type="email"
                value={data.email}
                onChange={(e) => handleInputChange('email', e.target.value)}
                placeholder="seu@email.com"
                disabled={loading}
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password">Senha *</Label>
              <Input
                id="password"
                type="password"
                value={data.password}
                onChange={(e) => handleInputChange('password', e.target.value)}
                placeholder="Mínimo 12 caracteres"
                disabled={loading}
                required
                minLength={12}
              />
              {data.password && !validatePassword(data.password).isValid && (
                <div className="text-sm text-destructive space-y-1">
                  {validatePassword(data.password).errors.map((error, idx) => (
                    <p key={idx}>• {error}</p>
                  ))}
                </div>
              )}
            </div>

            <div className="space-y-3 pt-4 border-t">
              <div className="flex items-center gap-2 text-sm">
                <Building2 className="w-4 h-4 text-muted-foreground" />
                <span className="text-muted-foreground">Empresa:</span>
                <span className="font-medium">Empresa de Demonstração</span>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <Clock className="w-4 h-4 text-muted-foreground" />
                <span className="text-muted-foreground">Validade:</span>
                <span className="font-medium">7 dias a partir do cadastro</span>
              </div>
            </div>

            <div className="flex gap-3 pt-4">
              <Button
                variant="outline"
                onClick={handleClose}
                disabled={loading}
                className="flex-1"
              >
                Cancelar
              </Button>
              <Button
                onClick={handleSubmit}
                disabled={!isFormValid() || loading}
                className="flex-1"
              >
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Criando...
                  </>
                ) : (
                  <>
                    <Eye className="mr-2 h-4 w-4" />
                    Acessar Demonstração
                  </>
                )}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}